import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  fetchChildren, fetchAncestors,
  buildHierarchy, computeDepths,
  fetchCurrentUserInfo, fetchProfile, isAdminUser,
  countUserNetwork, fetchUserNetworkPage,
} from "../script/rpc";
import MlmNode from "./MlmNode";
import InfoDrawer from "./InfoDrawer";

const nodeTypes = { mlmNode: MlmNode };
const PAGE_SIZE = 50;

const EDGE_COLORS = [
  "#7c3aed", "#0ea5e9", "#059669", "#d97706", "#db2777", "#0891b2", "#65a30d",
];

/* ═══════════════════════════════════════════════════════
   TREE LAYOUT — Reingold-Tilford
   Works for any number of children (2, 5, 20…)
   Parent always centred over its children span.
   ═══════════════════════════════════════════════════════ */
const NODE_W = 220;
const NODE_H = 200;

function computeWidth(node) {
  if (!node.children || node.children.length === 0) { node._w = 1; return; }
  node.children.forEach(computeWidth);
  node._w = node.children.reduce((s, c) => s + c._w, 0);
}

function placeNodes(node, depth, left, rfNodes, rfEdges) {
  const cx = (left + node._w / 2) * NODE_W;
  const y = depth * NODE_H;

  rfNodes.push({
    id: String(node.id),
    type: "mlmNode",
    position: { x: cx - 100, y },
    data: { member: node, depth, isSelected: false, expanded: false, loadingChildren: false, onSelect: () => { }, onExpand: () => { } },
  });

  if (node.parent_id && node.parent_id[0]) {
    const col = EDGE_COLORS[Math.max(0, depth - 1) % EDGE_COLORS.length];
    rfEdges.push({
      id: `e-${node.parent_id[0]}-${node.id}`,
      source: String(node.parent_id[0]),
      target: String(node.id),
      type: "smoothstep",
      style: { stroke: col, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: col, width: 14, height: 14 },
    });
  }

  let childLeft = left;
  (node.children || []).forEach(child => {
    placeNodes(child, depth + 1, childLeft, rfNodes, rfEdges);
    childLeft += child._w;
  });
}

function buildLayout(roots) {
  const rfNodes = [], rfEdges = [];
  roots.forEach(computeWidth);
  let left = 0;
  roots.forEach(root => {
    placeNodes(root, 0, left, rfNodes, rfEdges);
    left += root._w;
  });
  return { rfNodes, rfEdges };
}

/* ═══════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════ */
function fmtCur(val) {
  if (!val) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
}

/* ═══════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════ */
export default function MlmTree({ uid, password, onLogout }) {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // All fetched records: id → record
  const allRecords = useRef({});
  // Which node IDs have been expanded (children fetched)
  const expandedIds = useRef(new Set());
  // Which node IDs are currently loading children
  const loadingIds = useRef(new Set());
  // Root treepath for server-side pagination
  const rootTreepath = useRef(null);
  // Current pagination offset
  const pageOffset = useRef(0);

  const [totalCount, setTotalCount] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [loadedDescendants, setLoadedDescendants] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [search, setSearch] = useState("");
  const [, forceRender] = useState(0); // trigger re-layout

  /* ── Rebuild ReactFlow nodes + edges from allRecords ── */
  const rebuildTree = useCallback((cbSelect, cbExpand) => {
    const records = Object.values(allRecords.current);
    const roots = computeDepths(buildHierarchy(records));
    const { rfNodes, rfEdges } = buildLayout(roots);

    const withData = rfNodes.map(n => ({
      ...n,
      data: {
        ...n.data,
        isSelected: selectedMember && String(selectedMember.id) === n.id,
        expanded: expandedIds.current.has(Number(n.id)),
        loadingChildren: loadingIds.current.has(Number(n.id)),
        onSelect: cbSelect,
        onExpand: cbExpand,
      },
    }));

    setNodes(withData);
    setEdges(rfEdges);
  }, [selectedMember, setNodes, setEdges]);

  /* ── Callbacks (stable refs) ── */
  const handleSelectRef = useRef(null);
  const handleExpandRef = useRef(null);
  const rebuildRef = useRef(null);
  rebuildRef.current = rebuildTree;

  /* Forward-declared so nodes can reference them before mount */
  handleSelectRef.current = (member) => setSelectedMember(member);

  handleExpandRef.current = async (member) => {
    const id = member.id;

    if (expandedIds.current.has(id)) {
      // ── Collapse: remove all descendants from allRecords ──
      const toRemove = new Set();
      function collect(parentId) {
        Object.values(allRecords.current).forEach(r => {
          if (r.parent_id && r.parent_id[0] === parentId) {
            toRemove.add(r.id);
            expandedIds.current.delete(r.id);
            collect(r.id);
          }
        });
      }
      collect(id);
      toRemove.forEach(rid => delete allRecords.current[rid]);
      expandedIds.current.delete(id);
      setLoadedCount(Object.keys(allRecords.current).length);
      rebuildRef.current(
        m => handleSelectRef.current(m),
        m => handleExpandRef.current(m),
      );
      return;
    }

    // ── Expand: fetch children from server ──
    loadingIds.current.add(id);
    forceRender(c => c + 1);                         // show spinner
    rebuildRef.current(m => handleSelectRef.current(m), m => handleExpandRef.current(m));

    try {
      const children = await fetchChildren(uid, password, id);
      children.forEach(c => { allRecords.current[c.id] = c; });
      expandedIds.current.add(id);
    } catch (e) {
      console.error("Failed to fetch children:", e);
    } finally {
      loadingIds.current.delete(id);
    }

    setLoadedCount(Object.keys(allRecords.current).length);
    rebuildRef.current(
      m => handleSelectRef.current(m),
      m => handleExpandRef.current(m),
    );
  };

  /* ── Initial load ── */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const userInfo = await fetchCurrentUserInfo(uid, password);
        if (!userInfo || !userInfo.partner_id) {
          throw new Error("Could not fetch user information");
        }

        const partnerId = userInfo.partner_id[0];
        const [profile, adminCheck] = await Promise.all([
          fetchProfile(uid, password, partnerId),
          isAdminUser(uid, password),
        ]);

        // Only redirect non-admin users who have no parent (not yet set up)
        if (!adminCheck && profile && !profile.parent_id) {
          navigate("/profile", { replace: true });
          return;
        }

        // Fetch ancestors (path to root) — skip if admin has no parent
        const ancestors = (profile.parent_id && profile.parent_id[0])
          ? await fetchAncestors(uid, password, [{ ...profile, id: partnerId }])
          : [];
        if (!alive) return;

        // Find root of the network
        const allKnown = [profile, ...ancestors];
        const knownMap = {};
        allKnown.forEach(r => { knownMap[r.id] = r; });

        let rootId = partnerId;
        ancestors.forEach(a => {
          if (!a.parent_id || !a.parent_id[0] || !knownMap[a.parent_id[0]]) {
            rootId = a.id;
          }
        });

        // Find root record to get its treepath for server-side pagination
        const rootRecord = knownMap[rootId] || profile;
        const treepath = rootRecord.mlm_treepath || null;
        rootTreepath.current = treepath;

        // ── Fast parallel fetch: count + first page ──
        const [total, firstPage] = await Promise.all([
          countUserNetwork(uid, password, treepath),
          fetchUserNetworkPage(uid, password, treepath, { offset: 0, limit: PAGE_SIZE }),
        ]);
        if (!alive) return;

        setTotalCount(total);

        // Seed allRecords with root + ancestors
        allKnown.forEach(r => { allRecords.current[r.id] = r; });

        // Load first page and mark parents expanded
        firstPage.forEach(r => {
          allRecords.current[r.id] = r;
          if (r.parent_id && r.parent_id[0]) expandedIds.current.add(r.parent_id[0]);
        });
        expandedIds.current.add(rootId);

        pageOffset.current = firstPage.length;
        setLoadedDescendants(firstPage.length);

        const userName = userInfo.name || "User";
        setCurrentUserName(userName);
        setLoadedCount(Object.keys(allRecords.current).length);

      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [uid, password, navigate]);

  /* ── Rebuild whenever loadedCount or selectedMember changes ── */
  useEffect(() => {
    if (loading) return;
    rebuildRef.current(
      m => handleSelectRef.current(m),
      m => handleExpandRef.current(m),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedCount, loadedDescendants, selectedMember, loading]);

  /* ── Load More — fetch next page from server ── */
  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const nextPage = await fetchUserNetworkPage(
        uid, password, rootTreepath.current,
        { offset: pageOffset.current, limit: PAGE_SIZE }
      );
      nextPage.forEach(r => {
        allRecords.current[r.id] = r;
        if (r.parent_id && r.parent_id[0]) expandedIds.current.add(r.parent_id[0]);
      });
      pageOffset.current += nextPage.length;
      setLoadedDescendants(d => d + nextPage.length);
      setLoadedCount(Object.keys(allRecords.current).length);
    } catch (e) {
      console.error("Load more failed:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [uid, password]);

  /* ── Search ── */
  const handleSearch = useCallback(val => {
    setSearch(val);
    if (!val.trim()) return;
    const hit = Object.values(allRecords.current).find(r =>
      r.name.toLowerCase().includes(val.toLowerCase()) ||
      (r.mlm_sequence || "").includes(val.trim())
    );
    if (hit) setSelectedMember(hit);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f8fafc", fontFamily: "'Inter',system-ui,sans-serif" }}>

      {/* ── Header ───────────────────────────────────────── */}
      {/* ── Header Styles ── */}
      <style>{`
        .hdr-btn { display: flex; align-items: center; gap: 8px; padding: 7px 16px; border-radius: 8px; border: 1px solid #e2e8f0; background: #ffffff; color: #475569; font-size: 14.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; text-decoration: none; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .hdr-btn:hover { background: #f8fafc; border-color: #cbd5e1; color: #0f172a; }
        .hdr-danger-btn { display: flex; align-items: center; gap: 8px; padding: 7px 16px; border-radius: 8px; border: 1px solid #fecdd3; background: #fff5f5; color: #e11d48; font-size: 14.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .hdr-danger-btn:hover { background: #fee2e2; border-color: #fca5a5; color: #be123c; }
        .load-more-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 8px; border: 1px solid transparent; background: #e0e7ff; color: #4338ca; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
        .load-more-btn:hover:not(:disabled) { background: #c7d2fe; color: #3730a3; }
        .load-more-btn:disabled { opacity: 0.7; cursor: not-allowed; }
      `}</style>

      {/* ── Extremely Clean SaaS Header ── */}
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 24px", height: 68, flexShrink: 0,
        background: "#ffffff", borderBottom: "1px solid #e2e8f0",
        position: "sticky", top: 0, zIndex: 10
      }}>
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Logo & Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: "#6366f1",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              boxShadow: "0 2px 4px rgba(99, 102, 241, 0.2)"
            }}>
              <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="4" r="2.5" fill="white" />
                <circle cx="4" cy="15" r="2.5" fill="white" fillOpacity=".8" />
                <circle cx="16" cy="15" r="2.5" fill="white" fillOpacity=".8" />
                <line x1="10" y1="6.5" x2="4" y2="12.5" stroke="white" strokeWidth="1.5" />
                <line x1="10" y1="6.5" x2="16" y2="12.5" stroke="white" strokeWidth="1.5" />
              </svg>
            </div>
            <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px", whiteSpace: "nowrap" }}>
              MLM Network Tree
            </span>
          </div>

          <div style={{ width: 1, height: 20, background: "#e2e8f0", margin: "0 6px" }} />

          {/* Members Stats */}
          {!loading && !error && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: "#64748b", whiteSpace: "nowrap" }}>
                Showing <span style={{ color: "#0f172a", fontWeight: 700 }}>{currentUserName}</span>'s network
                {totalCount > 0 && <span style={{ color: "#94a3b8" }}> ({loadedDescendants}/{totalCount})</span>}
              </div>

              {totalCount > 50 && loadedDescendants < totalCount && (
                <button onClick={handleLoadMore} disabled={loadingMore} className="load-more-btn">
                  {loadingMore ? (
                    <><MiniSpinner /> Loading…</>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>
                      Load More
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          
          <a href="/transactions" className="hdr-btn">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4M7 4L3 8M7 4L11 8"></path><path d="M17 8V20M17 20L21 16M17 20L13 16"></path></svg>
            Transactions
          </a>

          <a href="/profile" className="hdr-btn">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            Profile
          </a>

          <div style={{ width: 1, height: 20, background: "#e2e8f0", margin: "0 8px" }} />

          {currentUserName && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "4px", borderRadius: 24,
              fontSize: "14.5px", fontWeight: 600, color: "#1e293b",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: "#0ea5e9",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0,
              }}>
                {currentUserName[0].toUpperCase()}
              </div>
              <span style={{ marginRight: 6 }}>{currentUserName.split(" ")[0]}</span>
            </div>
          )}

          <button onClick={onLogout} className="hdr-danger-btn" title="Sign out" style={{ padding: "7px 10px" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </header>

      {/* ── Loading progress bar ─────────────────────────── */}
      {!loading && totalCount > 50 && loadedDescendants < totalCount && (
        <div style={{ height: 3, background: "#f1f5f9", flexShrink: 0 }}>
          <div style={{
            height: "100%", width: `${(loadedDescendants / totalCount) * 100}%`,
            background: "linear-gradient(90deg,#7c3aed,#0ea5e9)",
            transition: "width 0.4s ease",
          }} />
        </div>
      )}

      {/* ── Canvas ───────────────────────────────────────── */}
      <div style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#7c3aed", animation: "spin 0.85s linear infinite" }} />
            <p style={{ color: "#64748b", fontSize: 13 }}>Loading your network…</p>
          </div>
        )}

        {error && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 40 }}>⚠️</div>
            <h3 style={{ color: "#dc2626", fontSize: 16 }}>Connection failed</h3>
            <p style={{ color: "#64748b", maxWidth: 440, fontSize: 13 }}>{error}</p>
            <button onClick={() => window.location.reload()}
              style={{ marginTop: 12, padding: "8px 20px", borderRadius: 8, background: "#7c3aed", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600 }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView fitViewOptions={{ padding: 0.18 }}
            minZoom={0.05} maxZoom={2}
            nodesDraggable nodesConnectable={false}
            panOnScroll={true}
            zoomOnPinch={true}
            attributionPosition="bottom-right"
          >
            <Background color="#cbd5e1" gap={20} size={1} style={{ background: "#f1f5f9" }} />
            <Controls style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} />
            <MiniMap
              nodeColor={n => EDGE_COLORS[(n.data?.depth ?? 0) % EDGE_COLORS.length]}
              nodeBorderRadius={6}
              style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, cursor: "pointer" }}
              pannable
              zoomable
              nodeClick={(event, node) => {
                if (node?.data?.onSelect) {
                  node.data.onSelect(node.data.member);
                }
              }}
            />
          </ReactFlow>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────── */}
     
        <footer style={{
          padding: "8px 24px", flexShrink: 0,
          background: "#fff", borderTop: "1px solid #e2e8f0",
          fontSize: 14, color: "#94a3b8",
        }}>
          {/* Controls Hints */}
          {/* <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
             <span>🖱 Scroll to pan • Pinch to zoom</span>
             <span>👆 Click for details</span>
             <span>▼ Show children</span>
          </div> */}

          {/* Powered by Branding */}
          <div style={{ textAlign:'center', alignItems: "center", gap: 4, fontWeight: 500, color: "#64748b" }}>
           
            <a href="https://xamta.co/" target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", textDecoration: "none", fontWeight: 500, letterSpacing: "0.2px" }}>
               Powered by XAMTA
            
            © {new Date().getFullYear()}
            </a>
          </div>
        </footer>
      <InfoDrawer member={selectedMember} onClose={() => setSelectedMember(null)} />
    </div>
  );
}

function StatPill({ color, label }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 5,
      padding: "4px 12px", borderRadius: 20,
      background: `${color}12`, border: `1.5px solid ${color}33`,
      fontSize: 11.5, fontWeight: 600, color, whiteSpace: "nowrap",
    }}>
      {label}
    </div>
  );
}

function MiniSpinner() {
  return <span style={{
    width: 12, height: 12, borderRadius: "50%",
    border: "1.5px solid currentColor", borderTopColor: "transparent",
    display: "inline-block", animation: "spin 0.7s linear infinite",
  }} />;
}
